import { Warning, Phone } from "@phosphor-icons/react";

export const SEV_RANK = { non_urgent: 0, urgent: 1, critical: 2 };

/** Evaluate answers against the triage questions (mirrors backend `triage_library.evaluate`). */
export function evaluateTriage(questions, answers) {
  let best = "non_urgent";
  const stops = [];
  const advice = [];
  for (const q of questions || []) {
    const a = answers[q.key];
    if (a == null || a === "") continue;
    const flag = (q.flags || {})[q.type === "yes_no" ? String(a).toLowerCase() : String(a)] || {};
    if (flag.severity && SEV_RANK[flag.severity] > SEV_RANK[best]) best = flag.severity;
    if (flag.stop) stops.push(flag.advice || "");
    else if (flag.advice) advice.push(flag.advice);
  }
  return { suggested: best, stops, advice, answered: Object.keys(answers).filter(k => (questions || []).some(q => q.key === k)).length };
}

/** Guided yes/no and multiple-choice questions for the chosen category, with safety-stop banners. */
export default function TriageQuestions({ questions, answers, onChange, accent }) {
  if (!questions?.length) return null;
  const ev = evaluateTriage(questions, answers);
  return (
    <div className="space-y-3" data-testid="triage-block">
      <h2 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">A few quick questions</h2>
      <div className="space-y-2">
        {questions.map(q => {
          const val = answers[q.key];
          const opts = q.type === "yes_no" ? [["yes", "Yes"], ["no", "No"]] : (q.options || []).map(o => [o, o]);
          const flag = val != null ? ((q.flags || {})[q.type === "yes_no" ? String(val).toLowerCase() : String(val)] || {}) : {};
          return (
            <div key={q.key} className={`rounded-lg border p-3 ${flag.stop ? "border-red-400 bg-red-50 dark:bg-red-950/30" : "border-border bg-background"}`} data-testid={`triage-q-${q.key}`}>
              <p className="text-sm font-medium mb-2">{q.text}</p>
              <div className="flex flex-wrap gap-1.5">
                {opts.map(([v, l]) => {
                  const on = val === v;
                  return (
                    <button key={v} type="button" onClick={() => onChange({ ...answers, [q.key]: v })}
                            className={`px-3 py-1.5 rounded-full text-sm border transition ${on ? "text-white border-transparent" : "border-border hover:bg-muted/40"}`}
                            style={on ? { backgroundColor: flag.stop ? "#dc2626" : accent } : undefined}
                            data-testid={`triage-${q.key}-${String(v).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{l}</button>
                  );
                })}
              </div>
              {flag.advice && !flag.stop && <p className="text-xs text-amber-800 dark:text-amber-300 mt-2">{flag.advice}</p>}
            </div>
          );
        })}
      </div>
      {ev.stops.length > 0 && (
        <div className="rounded-lg border-2 border-red-500 bg-red-50 dark:bg-red-950/40 p-4 space-y-2" data-testid="triage-stop-banner">
          <p className="text-sm font-bold text-red-700 dark:text-red-300 flex items-center gap-2"><Warning className="h-5 w-5" weight="fill" /> Please act on this first</p>
          {ev.stops.map((s, i) => <p key={i} className="text-sm text-red-800 dark:text-red-200">{s}</p>)}
          <p className="text-xs text-red-700 dark:text-red-300 flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> Then carry on below — we'll treat this as a critical emergency.</p>
        </div>
      )}
    </div>
  );
}
